/**
 * Retrieves the available products for passengers from a specific product group.
 *
 * This function searches through the provided product groups for the matching product ID
 * and returns the available products associated with that product for passengers.
 * If no matching product or available products are found, it returns an empty array.
 *
 */

export const getAvailableProductsForPassengers = (
  products: Content.Components['PaxProductGroupDto'][],
  productId: string,
): Content.Components['BindMapEntry'][] => {
  if (products.length === 0) return [];

  const availableProducts = products.find(
    product => product.id === productId,
  )?.availableProductsForPassengers;

  return availableProducts ?? [];
};

/**
 * Compares current and previous passenger product selections and returns
 * a patch product body request that reflects the changes with removed and added products.
 *
 * - If passengersWithProductSelected is empty, all current selections are considered "productsToAdd".
 * - If there are no changes between passengers and passengersWithProductSelected, returns null.
 */

export const getSelectionChangesSingleChoice = (
  passengers: Content.Components['GenericProductPassengerDto'][],
  passengersWithProductSelected: Content.Components['PassengerDto'][],
  categoryIds: number[],
): Booking.Components['PatchProductBodyRequest'] | null => {
  // Build current selection map: paxHash -> Set of bindIds
  const currentByPax: Record<string, Set<string>> = {};

  passengers
    .filter(passenger => passenger.isSelected && !passenger.isDisabled)
    .forEach(({ passenger, relevantPassengerBookingProducts = [] }) => {
      const paxHash = passenger.uniquePaxHash;

      if (!currentByPax[paxHash]) currentByPax[paxHash] = new Set();

      relevantPassengerBookingProducts
        .filter(product => product.isSelected)
        .forEach(({ bindId }) => {
          currentByPax[paxHash].add(bindId);
        });
    });

  // Build previous selection map: paxHash -> Set of bindIds
  const previousByPax: Record<string, Set<string>> = {};

  passengersWithProductSelected.forEach(passenger => {
    const paxHash = passenger.uniquePaxHash;
    const filteredSelectedProducts = passenger.filteredSelectedProducts || [];

    if (!previousByPax[paxHash]) previousByPax[paxHash] = new Set();

    filteredSelectedProducts.forEach(({ bindId }) => {
      previousByPax[paxHash].add(bindId);
    });
  });

  // Collect all unique paxHashes
  const allPaxHashes = new Set([
    ...Object.keys(currentByPax),
    ...Object.keys(previousByPax),
  ]);

  const passengersBody: Booking.Components['PatchProductBody'][] = [];

  // For each paxHash, determine what products to add or remove
  allPaxHashes.forEach(paxHash => {
    const currentProducts = currentByPax[paxHash] || new Set<string>();
    const previousProducts = previousByPax[paxHash] || new Set<string>();

    // Find products to add (in current but not in previous)
    const productsToAdd = Array.from(currentProducts).filter(
      bindId => !previousProducts.has(bindId),
    );

    // Find products to remove (in previous but not in current)
    const productsToRemove = Array.from(previousProducts).filter(
      bindId => !currentProducts.has(bindId),
    );

    // Only add to body if there are changes
    if (productsToAdd.length > 0 || productsToRemove.length > 0) {
      passengersBody.push({
        categoriesToRemove: categoryIds,
        productsToAdd: productsToAdd.length > 0 ? productsToAdd : null,
        productsToRemove: productsToRemove.length > 0 ? productsToRemove : null,
        uniquePaxHash: paxHash,
      });
    }
  });

  // Return null if no changes
  if (passengersBody.length === 0) return null;

  return { passengers: passengersBody };
};

/**
 * Compares current and previous passenger product selections for multiple choice and returns
 * a patch product body request that reflects the changes with removed and added products.
 *
 * - If passengersWithProductSelected is empty, all current selections are considered "productsToAdd".
 * - If there are no changes between passengers and passengersWithProductSelected, returns null.
 */

export const getSelectionChangesMultipleChoice = (
  passengers: Content.Components['GenericProductPassengerDto'][],
  passengersWithProductSelected: Content.Components['PassengerDto'][],
  categoryIds: number[],
): Booking.Components['PatchProductBodyRequest'] | null => {
  // Build current selection map: paxHash -> Set of bindIds
  const currentByPax: Record<string, Set<string>> = {};

  passengers.forEach(({ passenger, relevantPassengerBookingProducts = [] }) => {
    const paxHash = passenger.uniquePaxHash;

    if (!currentByPax[paxHash]) currentByPax[paxHash] = new Set();

    relevantPassengerBookingProducts
      .filter(product => product.isSelected)
      .forEach(({ bindId }) => {
        currentByPax[paxHash].add(bindId);
      });
  });

  // Build previous selection map: paxHash -> Set of bindIds
  const previousByPax: Record<string, Set<string>> = {};

  passengersWithProductSelected.forEach(passenger => {
    const paxHash = passenger.uniquePaxHash;
    const filteredSelectedProducts = passenger.filteredSelectedProducts || [];

    if (!previousByPax[paxHash]) previousByPax[paxHash] = new Set();

    filteredSelectedProducts.forEach(({ bindId }) => {
      previousByPax[paxHash].add(bindId);
    });
  });

  // Collect all unique paxHashes
  const allPaxHashes = new Set([
    ...Object.keys(currentByPax),
    ...Object.keys(previousByPax),
  ]);

  const passengersBody: Booking.Components['PatchProductBody'][] = [];

  // For each paxHash, determine what products to add or remove
  allPaxHashes.forEach(paxHash => {
    const currentProducts = currentByPax[paxHash] || new Set<string>();
    const previousProducts = previousByPax[paxHash] || new Set<string>();

    // Find products to add (in current but not in previous)
    const productsToAdd = Array.from(currentProducts).filter(
      bindId => !previousProducts.has(bindId),
    );

    // Find products to remove (in previous but not in current)
    const productsToRemove = Array.from(previousProducts).filter(
      bindId => !currentProducts.has(bindId),
    );

    // Only add to body if there are changes
    if (productsToAdd.length > 0 || productsToRemove.length > 0) {
      passengersBody.push({
        categoriesToRemove: categoryIds,
        productsToAdd: productsToAdd.length > 0 ? productsToAdd : null,
        productsToRemove: productsToRemove.length > 0 ? productsToRemove : null,
        uniquePaxHash: paxHash,
      });
    }
  });

  // Return null if no changes
  if (passengersBody.length === 0) return null;

  return { passengers: passengersBody };
};

/**
 * Generates a patch product body request from a list of products for all passengers.
 *
 * This function processes products available for passengers and organizes them by unique passenger hash.
 * It creates a map where each passenger (identified by their unique hash) is associated with a set of
 * product bind IDs that should be added to their booking.
 *
 */

export const generatePatchProductBodyRequestAddForAllSingleSelect = (
  productsForAllPax: Content.Components['PaxProductGroupDto'][],
  categoryIds: number[],
): Booking.Components['PatchProductBodyRequest'] => {
  const passengersMap = new Map<string, Set<string>>();

  productsForAllPax.forEach(productForAllPax => {
    productForAllPax.availableProductsForPassengers.forEach(
      productForPassenger => {
        const paxHashes = productForPassenger.uniquePaxHash ?? [];

        paxHashes.forEach(paxHash => {
          if (!passengersMap.has(paxHash)) {
            passengersMap.set(paxHash, new Set());
          }

          if (productForPassenger.bindId) {
            passengersMap.get(paxHash)!.add(productForPassenger.bindId);
          }
        });
      },
    );
  });

  const passengers: Booking.Components['PatchProductBody'][] = Array.from(
    passengersMap.entries(),
  ).map(([paxHash, bindIds]) => ({
    categoriesToRemove: categoryIds,
    productsToAdd: Array.from(bindIds),
    productsToRemove: null,
    uniquePaxHash: paxHash,
  }));

  return { passengers };
};

/**
 * Generates a patch product body request for deleting all single select products for passengers.
 *
 * This function processes an array of passengers with their selected products and creates
 * a request body that removes all single select products associated with each unique passenger
 * while removing the selected categories.
 *
 */

export const generatePatchProductBodyRequestDeleteForAllSingleSelect = (
  passengers: Content.Components['GenericProductPassengerDto'][],
): Booking.Components['PatchProductBodyRequest'] => {
  const passengersMap = new Map<string, Set<string>>();

  passengers.forEach(({ passenger }) => {
    const uniquePaxHash = passenger.uniquePaxHash;
    const filteredSelectedProducts = passenger.filteredSelectedProducts;

    filteredSelectedProducts.forEach(({ bindId }) => {
      if (!passengersMap.has(uniquePaxHash)) {
        passengersMap.set(uniquePaxHash, new Set());
      }

      passengersMap.get(uniquePaxHash)!.add(bindId);
    });
  });

  const passengersBody: Booking.Components['PatchProductBody'][] = Array.from(
    passengersMap.entries(),
  ).map(([paxHash, bindIds]) => ({
    categoriesToRemove: null,
    productsToAdd: null,
    productsToRemove: Array.from(bindIds),
    uniquePaxHash: paxHash,
  }));

  return { passengers: passengersBody };
};

/**
 * Generates a patch product body request for adding products to all passengers with multiple selection.
 *
 * This function processes available products for passengers and creates a structured request body
 * that maps each passenger (identified by their unique hash) to their selected products (bind IDs).
 * It also specifies which categories should be removed for all passengers.
 *
 */

export const generatePatchProductBodyRequestAddForAllMultipleSelect = (
  availableProductsForPassengers: Content.Components['BindMapEntry'][],
  categoryIds: number[],
): Booking.Components['PatchProductBodyRequest'] => {
  const passengersMap = new Map<string, Set<string>>();

  availableProductsForPassengers.forEach(productForPassenger => {
    const paxHashes = productForPassenger.uniquePaxHash ?? [];

    paxHashes.forEach(paxHash => {
      if (!passengersMap.has(paxHash)) {
        passengersMap.set(paxHash, new Set());
      }

      if (productForPassenger.bindId) {
        passengersMap.get(paxHash)!.add(productForPassenger.bindId);
      }
    });
  });

  const passengers: Booking.Components['PatchProductBody'][] = Array.from(
    passengersMap.entries(),
  ).map(([paxHash, bindIds]) => ({
    categoriesToRemove: categoryIds,
    productsToAdd: Array.from(bindIds),
    productsToRemove: null,
    uniquePaxHash: paxHash,
  }));

  return { passengers };
};

/**
 * Generates a patch product body request for deleting all multiple select products for passengers.
 *
 * This function processes an array of passengers with their selected products and creates
 * a request body that removes all products associated with each unique passenger.
 *
 */

export const generatePatchProductBodyRequestDeleteForAllMultipleSelect = (
  passengers: Content.Components['GenericProductPassengerDto'][],
): Booking.Components['PatchProductBodyRequest'] => {
  const passengersMap = new Map<string, Set<string>>();

  passengers.forEach(({ passenger }) => {
    const uniquePaxHash = passenger.uniquePaxHash;
    const filteredSelectedProducts = passenger.filteredSelectedProducts;

    filteredSelectedProducts.forEach(({ bindId }) => {
      if (!passengersMap.has(uniquePaxHash)) {
        passengersMap.set(uniquePaxHash, new Set());
      }

      passengersMap.get(uniquePaxHash)!.add(bindId);
    });
  });

  const passengersBody: Booking.Components['PatchProductBody'][] = Array.from(
    passengersMap.entries(),
  ).map(([paxHash, bindIds]) => ({
    categoriesToRemove: null,
    productsToAdd: null,
    productsToRemove: Array.from(bindIds),
    uniquePaxHash: paxHash,
  }));

  return { passengers: passengersBody };
};
